/**
 * Ops dashboard base path on the Team hub.
 *
 * team.thetabletop.in serves the staff hub at `/` and Ops under `/ops` from one
 * bundle and one docroot. Ops loads mount react-router with `/ops` as its
 * basename, so every `to=` and `navigate()` inside the dashboard re-bases for
 * free. Only code that reads or writes `window.location` directly sees the
 * prefix, and that code uses the helpers here.
 *
 * On manage.thetabletop.in nothing is prefixed: the dashboard stays at the root
 * so already-delivered links keep resolving. See docs/ops/outbound-url-inventory.md.
 */

import { isTeamHubHost } from "@/lib/teamHub";

export const OPS_BASENAME = "/ops";

const OPS_PATH_RE = /^\/ops(?=\/|$)/;

function currentPathname() {
  return typeof window !== "undefined" ? window.location.pathname || "/" : "";
}

/** True for `/ops` and `/ops/...`, false for `/opsomething`. */
export function isOpsPath(pathname = currentPathname()) {
  return OPS_PATH_RE.test(String(pathname || ""));
}

/**
 * The basename react-router should mount with for this page load.
 *
 * Resolved once at import: a basename only changes across a full navigation,
 * because react-router will not route outside the base it was given.
 */
export const OPS_MOUNTED =
  typeof window !== "undefined" &&
  isOpsPath(currentPathname()) &&
  // Guarded on the host so a stray /ops on the Manage host does not quietly
  // serve a second copy of the dashboard at a second address.
  isTeamHubHost();

/**
 * Strip the Ops prefix so raw `window.location.pathname` reads can keep using
 * the same route patterns on both hosts.
 */
export function appPathname(pathname = currentPathname()) {
  const path = String(pathname || "");
  if (!isOpsPath(path)) return path;
  return path.replace(OPS_PATH_RE, "") || "/";
}

/**
 * Prefix an in-app path with the Ops base when this load is mounted under it.
 * For hard navigations and `window.location` writes, which bypass the router.
 */
export function withOpsBase(path) {
  const p = String(path || "/");
  if (!OPS_MOUNTED) return p;
  if (!p.startsWith("/") || p.startsWith("//")) return p;
  return `${OPS_BASENAME}${p === "/" ? "" : p}` || OPS_BASENAME;
}
